import { Facing } from "odyssey-shared";

/**
 * Whether a thing is faced, the test that picks which of several usable things in reach E and the interaction prompt
 * name (P1.9-T29): the faced one, else the nearest to the character's feet, ties by handle (`outranks`). The choice
 * depends on nothing the network orders, so a keyframe laying the view out differently never swaps it. A thing is faced
 * when the direction from the character's feet to the thing's feet point lies within 45 degrees of the facing
 * direction, edges included, the arc of Strike's cone (ADR-073 decision 7); the four facings divide the ground with
 * no gap, and a thing exactly on an edge between two facings counts as faced by both.
 *
 * @param facing The character's facing.
 * @param dx The thing's feet across minus the character's, in sixteenths.
 * @param dy The thing's feet down minus the character's, in sixteenths.
 * @returns True when the direction to the thing is within 45 degrees of the facing, edges included; false for a thing
 *   at the character's own feet, which lies in no direction.
 */
export function faces(facing: Facing, dx: number, dy: number): boolean {
  switch (facing) {
    case Facing.Down:
      return dy > 0 && Math.abs(dx) <= dy;
    case Facing.Up:
      return dy < 0 && Math.abs(dx) <= -dy;
    case Facing.Left:
      return dx < 0 && Math.abs(dy) <= -dx;
    default:
      return dx > 0 && Math.abs(dy) <= dx;
  }
}

/**
 * The squared distance between two feet points, so two things compare without a root.
 *
 * @param dx Across, in sixteenths.
 * @param dy Down, in sixteenths.
 * @returns The squared length.
 */
export function distanceSquared(dx: number, dy: number): number {
  return dx * dx + dy * dy;
}

/**
 * Whether a candidate beats the best so far: faced beats not faced, then the nearer, then the lower handle.
 *
 * @param faced Whether the candidate is faced.
 * @param distance The candidate's squared distance.
 * @param handle The candidate's handle.
 * @param bestFaced Whether the best so far is faced.
 * @param bestDistance The best's squared distance.
 * @param bestHandle The best's handle.
 * @returns True when the candidate outranks the best.
 */
export function outranks(faced: boolean, distance: number, handle: number, bestFaced: boolean, bestDistance: number, bestHandle: number): boolean {
  if (faced !== bestFaced) {
    return faced;
  }
  return distance !== bestDistance ? distance < bestDistance : handle < bestHandle;
}
